<template>
  <div v-if="paymentMethod" class="payment-method-logo-component" :class="{ 'small-logo': small }">
    <template v-if="paymentMethod.custom_logo">
      <div class="payment-methods-logo">
        <img :src="paymentMethod.custom_logo" />
      </div>
    </template>

    <template v-else-if="getPaymentMethodLogo(paymentMethod) !== null">
      <div v-if="Array.isArray(getPaymentMethodLogo(paymentMethod)?.logo)" class="flex gap-1">
        <div v-for="(logo, idx) in getPaymentMethodLogo(paymentMethod)?.logo" :key="idx">
          <img :src="logo" :alt="t('payment.payment_methods.payment_card')" />
        </div>
      </div>
      <div v-else class="payment-methods-logo" :class="{ 'justify-end': rightAlign }">
        <img :src="getPaymentMethodLogo(paymentMethod)?.logo" :alt="getPaymentMethodLogo(paymentMethod)?.name" />
      </div>
    </template>

    <template v-else>
      <span v-html="debracketize(context.option.label)" />
    </template>

    <template v-if="getPaymentMethodType(paymentMethod) === PaymentMethods.VisaMasterCard">
      <span v-text="t('payment.payment_by_card')" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { PaymentMethodInterface } from '~/types/checkoutTypes'
import { PaymentMethods } from '~/enums/PaymentMethods'
import LogoMasterCard from 'assets/images/logo-mastercard.svg'
import LogoVisa from 'assets/images/logo-visa.svg'
import LogoPayPal from 'assets/images/logo-paypal.svg'
import LogoPaySera from 'assets/images/logo-paysera.png'
import debracketize from '~/utils/debracketize'
import { useLang } from '~/composables/useLang'
import type { SummaryQuotePaymentInterface } from '~/types/quoteTypes'

defineProps<{
  paymentMethod: PaymentMethodInterface | SummaryQuotePaymentInterface
  small?: boolean
  rightAlign?: boolean
}>()

const { t } = useLang()
const logos = await useAssetManager().getLogos()
function getPaymentMethodType(data: { type: number; payment_processor_id?: number }) {
  return data.payment_processor_id ? data.payment_processor_id : data.type
}

function getPaymentMethodLogo(data: { type: number; payment_processor_id?: number }): {
  logo: string | Array<string>
  name: string
} | null {
  let value = null
  const type = getPaymentMethodType(data)

  switch (type) {
    case PaymentMethods.VisaMasterCard:
      value = {
        logo: [LogoMasterCard, LogoVisa],
        name: ['MasterCard', 'Visa'],
      }
      break
    case PaymentMethods.PayPal:
      value = {
        logo: LogoPayPal,
        name: 'Paypal',
      }
      break
    case PaymentMethods.PaySera:
      value = {
        logo: LogoPaySera,
        name: 'Paysera',
      }
      break
    case PaymentMethods.Balance:
      value = {
        logo: logos.logoBlack,
        name: t('confirm.payment.balance'),
      }
      break
  }

  return value
}
</script>

<style lang="postcss" scoped>
.payment-method-logo-component {
  @apply flex items-center gap-2;

  .payment-methods-logo {
    @apply flex h-[38px] w-[158px] bg-left bg-no-repeat bg-contain p-1;
    @apply dark:bg-white dark:rounded-sm dark:justify-center;
  }

  &.small-logo {
    .payment-methods-logo {
      @apply h-[28px] bg-right;
    }
  }
}
</style>
